import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'

import { inject, observer } from 'mobx-react'

import { IUserProject } from '~/client/graph'
import { AvatarSize } from '~/client/src/shared/components/Avatar/Avatar'
import { PopoverTagPicker } from '~/client/src/shared/components/PopoverTagPicker/PopoverTagPicker'
import {
  Field,
  FieldLabel,
} from '~/client/src/shared/components/shadcn-ui/field'
import { Tag } from '~/client/src/shared/components/Tag/Tag'
import {
  GroupingOption,
  ICurrentUserInfo,
  IFilterData,
  IUserMetadata,
  UserListSelector,
  useUserListSelectorData,
} from '~/client/src/shared/components/UserListSelector'
import UserProfilePreview from '~/client/src/shared/components/UserProfilePreview/UserProfilePreview'
import { TagType } from '~/client/src/shared/enums/TagType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import User from '~/client/src/shared/models/User'
import UserProject from '~/client/src/shared/models/UserProject'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import ProjectRolesStore from '~/client/src/shared/stores/domain/ProjectRoles.store'
import ProjectTeamsStore from '~/client/src/shared/stores/domain/ProjectTeams.store'
import ProjectTradesStore from '~/client/src/shared/stores/domain/ProjectTrades.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import { toast } from '~/client/src/shared/utils/toast'

// Order in which the system user tags are shown. Mirrors the backend system tag
// values (StruxHub.Graph UserTagValues): Project Coordinator, Project Champion.
const SYSTEM_USER_TAG_VALUES = ['Project Coordinator', 'Project Champion']

interface IRowProps {
  tagId: string
  label: string
  users: User[]
  userProjectsStore: UserProjectsStore
  userMetadata: Map<string, IUserMetadata>
  currentUser?: ICurrentUserInfo
  filterData: IFilterData
  // Bumped by the parent on every tag change (and on rollback). UserProject.tags
  // isn't observable, so this is what re-derives taggedUsers/selectedUserIds
  // immediately, rather than waiting for the user-project subscription echo.
  tagsVersion: number
  // Blocks input while a save is in flight. The mutation replaces the whole tags
  // array, so a second edit started before the first settles would race it.
  isSaving: boolean
  onApply: (tagId: string, userIds: Set<string>) => void
}

const UserTagPickerRow = observer((props: IRowProps) => {
  const {
    tagId,
    label,
    users,
    userProjectsStore,
    userMetadata,
    currentUser,
    filterData,
    tagsVersion,
    isSaving,
    onApply,
  } = props
  const [isOpen, setIsOpen] = useState(false)

  const taggedUsers = useMemo(
    () =>
      users.filter(user =>
        userProjectsStore.getByUser(user)?.tags?.includes(tagId),
      ),
    [users, userProjectsStore.list, tagId, tagsVersion],
  )

  const selectedUserIds = useMemo(
    () => new Set(taggedUsers.map(user => user.id)),
    [taggedUsers],
  )

  const handleApply = useCallback(
    (newIds: Set<string>) => {
      if (isSaving) {
        return
      }
      onApply(tagId, newIds)
      setIsOpen(false)
    },
    [onApply, tagId, isSaving],
  )

  const handleRemove = useCallback(
    (userId: string) => {
      if (isSaving) {
        return
      }
      const next = new Set(selectedUserIds)
      next.delete(userId)
      onApply(tagId, next)
    },
    [onApply, tagId, selectedUserIds, isSaving],
  )

  return (
    <Field className="tw:w-[350px]">
      <FieldLabel>{label}</FieldLabel>
      <PopoverTagPicker
        isOpen={isOpen}
        onOpen={() => !isSaving && setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        placeholder={Localization.translator.none}
        triggerLabel={label}
        popoverClassName="tw:w-[400px] tw:max-h-[800px] tw:min-h-[600px]"
        popoverContent={
          <UserListSelector
            selectedUserIds={selectedUserIds}
            users={users}
            userMetadata={userMetadata}
            currentUser={currentUser}
            filterData={filterData}
            title={label}
            showActions
            onClose={() => setIsOpen(false)}
            onApply={handleApply}
            className="tw:flex-1 tw:min-h-0"
            defaultGrouping={GroupingOption.COMPANY}
          />
        }
      >
        {taggedUsers.map(user => (
          <Tag
            key={user.id}
            avatar={
              <UserProfilePreview
                user={user}
                isCompact
                avatarSize={AvatarSize.XMinuscule}
              />
            }
            onDismiss={() => handleRemove(user.id)}
          >
            {user.fullName}
          </Tag>
        ))}
      </PopoverTagPicker>
    </Field>
  )
})

interface IProps {
  state?: InitialState
  userProjectsStore?: UserProjectsStore
  projectMembersStore?: ProjectMembersStore
  companiesStore?: CompaniesStore
  projectRolesStore?: ProjectRolesStore
  projectTeamsStore?: ProjectTeamsStore
  projectTradesStore?: ProjectTradesStore
  tagsStore?: TagsStore
}

// Assigns the system user tags (Project Coordinator, Project Champion) on the
// project Details page, using the same picker as the Approval Configuration.
// Toggling a tag persists to UserProject.tags; the Project Coordinator tag also
// drives the sign-up BIC fallback (resolved server-side).
const ProjectUserTagsBlock = observer((props: IProps) => {
  const {
    state,
    userProjectsStore,
    projectMembersStore,
    companiesStore,
    projectRolesStore,
    projectTeamsStore,
    projectTradesStore,
    tagsStore,
  } = props

  const { users, userMetadata, currentUser, filterData } =
    useUserListSelectorData({
      projectMembersStore,
      userProjectsStore,
      companiesStore,
      state,
      projectRolesStore,
      projectTeamsStore,
      projectTradesStore,
      tagsStore,
    })

  const userTags = tagsStore.tagListsByTagTypeMap[TagType.UserTag] || []
  const systemUserTagIds = tagsStore.systemUserTagIds

  // UserProject.tags isn't observable (matches roles/teams/trades), so an in-place
  // tag change can't repaint the picker via MobX. Bumping this version — which the
  // rows depend on — repaints them immediately.
  const [tagsVersion, setTagsVersion] = useState(0)

  // Only one save may be in flight. saveManyUserProjects replaces the whole tags
  // array, so two overlapping edits race and the loser's array silently wins — and a
  // rollback could restore another edit's unpersisted write rather than the server's
  // state. Blocking input while saving removes both by construction, which is why
  // there is no per-edit sequence tracking here.
  const [isSaving, setIsSaving] = useState(false)

  const setUsersForTag = useCallback(
    (tagId: string, newUserIds: Set<string>) => {
      const changed: IUserProject[] = []
      const rollbacks: {
        userProject: UserProject
        wroteHasTag: boolean
      }[] = []

      users.forEach(user => {
        const userProject = userProjectsStore.getByUser(user)
        if (!userProject) {
          return
        }

        const currentTags = userProject.tags || []
        const shouldHaveTag = newUserIds.has(user.id)
        if (currentTags.includes(tagId) === shouldHaveTag) {
          return
        }

        userProject.tags = shouldHaveTag
          ? [...currentTags, tagId]
          : currentTags.filter(id => id !== tagId)

        rollbacks.push({ userProject, wroteHasTag: shouldHaveTag })
        changed.push(userProject.toDto())
      })

      if (!changed.length) {
        return
      }

      // Repaint the optimistic in-place change now (tags aren't observable).
      setTagsVersion(v => v + 1)

      // If the save fails, undo this request's optimistic write so in-memory state
      // doesn't drift from the server, repaint the revert, and surface it — otherwise
      // the assignment looks applied but never persisted.
      setIsSaving(true)
      userProjectsStore
        .save(changed)
        .catch(() => {
          // No newer edit can exist: input is blocked until this settles, so undoing
          // this request's write restores the last server-known state.
          rollbacks.forEach(({ userProject, wroteHasTag }) => {
            const tags = userProject.tags || []
            userProject.tags = wroteHasTag
              ? tags.filter(id => id !== tagId)
              : [...tags, tagId]
          })
          setTagsVersion(v => v + 1)
          toast.error(Localization.translator.failed)
        })
        .finally(() => setIsSaving(false))
    },
    [users, userProjectsStore],
  )

  if (!userTags.length) {
    return null
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-3 tw:px-[30px] tw:py-2">
      {SYSTEM_USER_TAG_VALUES.map(value => {
        const tag = userTags.find(
          userTag => systemUserTagIds.has(userTag.id) && userTag.name === value,
        )
        if (!tag) {
          return null
        }

        return (
          <UserTagPickerRow
            key={tag.id}
            tagId={tag.id}
            label={tag.name}
            users={users}
            userProjectsStore={userProjectsStore}
            userMetadata={userMetadata}
            currentUser={currentUser}
            filterData={filterData}
            tagsVersion={tagsVersion}
            isSaving={isSaving}
            onApply={setUsersForTag}
          />
        )
      })}
    </div>
  )
})

export default inject(
  'state',
  'userProjectsStore',
  'projectMembersStore',
  'companiesStore',
  'projectRolesStore',
  'projectTeamsStore',
  'projectTradesStore',
  'tagsStore',
)(ProjectUserTagsBlock)
